import { type ReactNode, useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router';
import { useAuth, type AuthContextProps } from 'react-oidc-context';
import { devAuthUser } from '@config/env';
import { BrandLoader } from '@shared/ui';
import { routes } from '@config/routes';
import { renewSilently } from './useAccessToken';

interface AuthGuardProps {
  children: ReactNode;
}

export function AuthGuard({ children }: AuthGuardProps) {
  // Dev-auth shim — bypass Cognito entirely when VITE_DEV_AUTH_USER is set.
  if (devAuthUser) return <>{children}</>;

  return <CognitoGuard>{children}</CognitoGuard>;
}

function CognitoGuard({ children }: AuthGuardProps) {
  const auth = useAuth();
  const bootRenew = useBootRenew(auth);

  // Full-screen "Signing you in…" is for the INITIAL load only. Once authenticated, a silent token
  // renew (`automaticSilentRenew` → `activeNavigator === 'signinSilent'`, and the brief `isLoading`
  // it triggers) must NOT unmount the app — that blanked every page mid-session. So gate the
  // takeover on "not yet authenticated": while authenticated we keep rendering children and let the
  // renew happen invisibly in the background.
  if (!auth.isAuthenticated) {
    if (auth.isLoading || auth.activeNavigator || bootRenew === 'pending')
      return <FullPageStatus label="Signing you in…" />;
    // Unauthenticated and idle (first visit, signed out, or a failed/expired renew — auth.error
    // included): the in-app login page IS the recovery path, so always land there. Replaces the
    // old `auth.signinRedirect()` to the Cognito Hosted UI.
    return <Navigate to={routes.login} replace />;
  }

  return <>{children}</>;
}

/**
 * A stored session whose ACCESS token has expired but which still holds a refresh token — what a
 * "Keep me signed in" device looks like the morning after. Pure so the rule stays pinned by tests.
 */
export function canRenewAtBoot(user: AuthContextProps['user']): boolean {
  return !!user && user.expired === true && !!user.refresh_token;
}

/**
 * Boot-time renew of a remembered session (2026-09-13).
 *
 * "Keep me signed in" stores the refresh token in localStorage, but on the next cold boot
 * react-oidc-context reports a stored session whose access token has expired as UNauthenticated
 * (`isAuthenticated = user ? !user.expired : false`) and only loads it — it never tries a refresh —
 * and oidc-client-ts cancels its "expiring" timer for a token that is already expired at load, so
 * `automaticSilentRenew` never fires either. The old guard then bounced straight to /login, so a
 * remembered session never outlived the 1-hour access token while a 30-day refresh token sat
 * unused. This spends that refresh token ONCE before giving up: success flips `isAuthenticated`
 * via USER_LOADED and the app renders; failure falls through to the login page as before.
 *
 * Once per mount (ref, not state — StrictMode double-invokes effects in dev, and Cognito ROTATES
 * refresh tokens, so a second concurrent attempt would fail and could kill a good session). Shares
 * `renewSilently` with the API token resolver for the same reason.
 */
function useBootRenew(auth: AuthContextProps): 'pending' | 'settled' {
  const triedRef = useRef(false);
  const [settled, setSettled] = useState(false);

  useEffect(() => {
    if (auth.isAuthenticated || auth.isLoading || auth.activeNavigator) return;
    if (triedRef.current || !canRenewAtBoot(auth.user)) return;
    triedRef.current = true;
    void renewSilently(auth).finally(() => setSettled(true));
  }, [auth]);

  // Decided synchronously so the very first paint is the splash, never a redirect that unmounts
  // the guard before the effect can run.
  return canRenewAtBoot(auth.user) && !settled ? 'pending' : 'settled';
}

function FullPageStatus({ label }: { label: string }) {
  return (
    <div className="flex h-screen w-full flex-col items-center justify-center gap-4">
      <BrandLoader decorative />
      <p className="text-neutral-500">{label}</p>
    </div>
  );
}
